import { Box } from "@mui/material";
import i18next from "i18next";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../components/common/BriaButton/BriaButton";
import { FilterByOptions, PaginationOptions } from "../../../components/common/BriaTable/BriaTable";
import ProjectCard from "../../../components/common/ProjectCard/ProjectCard";
import RouterConstants from "../../../constants/RouterConstants";
import useSecureNavigate from "../../../hooks/useSecureNavigate";
import { useAppStore } from "../../../hooks/useStores";
import DataDashboardLayout from "../../../layout/DataDashboard/DataDashboardLayout";
import TgProjectActionsPopover from "./Dashboard/ActionsPopover/TgProjectActionsPopover";
import TgProjectsEmpty from "./EmptyState/TgProjectsEmpty";
import styles from "./TgProjects.module.scss";
import { ViewTgProjectLocationState } from "./Viewer/TgProjectViewer";
import { TgProject } from "./store/tg-projects-store";

const TgProjects = () => {
	const { t } = useTranslation("tg", { keyPrefix: "projects.dashboard" });
	const navigate = useSecureNavigate();
	const { tgStore, authStore } = useAppStore();
	const { tgProjectStore } = tgStore;
	const paginationOptions: PaginationOptions<TgProject> = {
		rowsPerPage: tgProjectStore.rowsPerPage,
		totalRows: tgProjectStore.paginatedProjects.total,
		loadNextPage: tgProjectStore.getOrgProjects,
	};
	const [filterBy, setFilterBy] = useState<FilterByOptions<TgProject>>();

	const openProject = (projectToView: TgProject) => {
		const viewProjectState: ViewTgProjectLocationState = { projectToView: toJS(projectToView) };
		navigate(
			`${RouterConstants.NEW_TG.PROJECTS.fullPath}/${projectToView.id}/${RouterConstants.NEW_TG.MODELS.path}`,
			{
				state: viewProjectState,
			},
		);
	};

	const openCreateProject = () => {
		navigate(RouterConstants.NEW_TG.PROJECTS.createPath);
	};

	const handleSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const updatedFilterBy: FilterByOptions<TgProject> = {
			...filterBy,
			search: {
				text: e.target.value,
				fields: ["project_name", "project_description", "ip_medium", "ip_type"],
			},
		};
		setFilterBy(updatedFilterBy);
		await tgProjectStore.getOrgProjects(1, updatedFilterBy);
	};

	return (
		<DataDashboardLayout
			className={styles.layout}
			primaryButton={
				<BriaButton buttonType="primaryMedium" onClick={openCreateProject}>
					{t("primaryButton")}
				</BriaButton>
			}
			secondaryButton={
				authStore.user?.isSuperAdmin() ? (
					<BriaButton
						buttonType="secondaryMedium"
						onClick={tgProjectStore.loadTgUsageReport}
						loading={tgProjectStore.loadingUsageReport}
						disabled={tgProjectStore.loadingUsageReport}
					>
						{t("secondaryButton")}
					</BriaButton>
				) : undefined
			}
			enableSearch
			searchInputVisible
			handleSearchChange={handleSearchChange}
			searchBoxPlaceholder={t("search.placeholder")}
			paginationOptions={paginationOptions}
			filterBy={filterBy}
			loading={tgProjectStore.loadingOrgProjects}
		>
			{!tgProjectStore.paginatedProjects.items.length ? (
				<TgProjectsEmpty />
			) : (
				<Box className={styles.cards}>
					{tgProjectStore.paginatedProjects.items.map((project) => {
						const cardFields = [
							{
								key: t("card.info.ip_medium"),
								value: i18next.t(`projects.editor.tgIpMediums.${project.ip_medium}`, { ns: "tg" }),
							},
							{
								key: t("card.info.ip_type"),
								value: i18next.t(`projects.editor.ipType.types.${project.ip_type}.name`, { ns: "tg" }),
							},
						];
						return (
							<ProjectCard
								key={project.id}
								name={project.project_name}
								description={project.project_description}
								cardFields={cardFields}
								onSecondaryAction={() => openProject(project)}
								secondaryButtonLabel={t("card.actions.primaryButton.nonEmpty")}
								actionsPopover={<TgProjectActionsPopover project={project} />}
							/>
						);
					})}
				</Box>
			)}
		</DataDashboardLayout>
	);
};

export default observer(TgProjects);
